import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const GREETING = {
  role: "assistant",
  text: "Hi there! I'm the TMG assistant. Ask me anything about our consulting, speaking, training, or AI offerings — or how to get started.",
};

const QUICK_PROMPTS = [
  "What do you offer?",
  "Tell me about The Rules of Money",
  "How do I book a call?",
];

function getSessionId() {
  let sid = localStorage.getItem("tmg_chat_session");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("tmg_chat_session", sid);
  }
  return sid;
}

/** Render **bold**, newlines, and internal /paths as links. */
function MessageText({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*|\/[a-z][a-z0-9\-/#]*)/g);
  return (
    <span>
      {parts.map((p, i) => {
        if (p.startsWith("**") && p.endsWith("**")) {
          return <strong key={i}>{p.slice(2, -2)}</strong>;
        }
        if (/^\/[a-z]/.test(p)) {
          return (
            <a key={i} href={p} className="underline decoration-[var(--tmg-gold)] underline-offset-2">
              {p}
            </a>
          );
        }
        return p.split("\n").map((line, j) => (
          <span key={`${i}-${j}`}>
            {j > 0 && <br />}
            {line}
          </span>
        ));
      })}
    </span>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const location = useLocation();
  const scrollRef = useRef(null);
  const loadedRef = useRef(false);

  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    if (open && !loadedRef.current) {
      loadedRef.current = true;
      fetch(`${API}/api/chat/history?session_id=${getSessionId()}`)
        .then((r) => r.json())
        .then((d) => {
          if (d.messages && d.messages.length) {
            setMessages([GREETING, ...d.messages]);
          }
        })
        .catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  if (isAdmin) return null;

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, session_id: getSessionId() }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === '"[DONE]"') break;
          try {
            const token = JSON.parse(payload);
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = {
                role: "assistant",
                text: next[next.length - 1].text + token,
              };
              return next;
            });
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: "assistant",
          text: "Sorry, something went wrong. You can reach us at info@tmariegreene.com.",
        };
        return next;
      });
    }
    setBusy(false);
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          data-testid="chat-open-btn"
          onClick={() => setOpen(true)}
          aria-label="Chat with us"
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[var(--tmg-obsidian)] text-[var(--tmg-gold)] border-2 border-[var(--tmg-gold)] shadow-[0_12px_32px_-8px_rgba(10,10,10,0.4)] flex items-center justify-center hover:scale-105 transition-transform duration-200"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-6 right-6 z-40 w-[calc(100vw-3rem)] max-w-sm h-[520px] max-h-[70vh] bg-white border border-[var(--tmg-line)] shadow-[0_32px_64px_-24px_rgba(10,10,10,0.35)] flex flex-col"
        >
          <div className="bg-[var(--tmg-obsidian)] text-[var(--tmg-ivory)] px-5 py-4 flex items-center justify-between border-b-2 border-[var(--tmg-gold)]">
            <div>
              <p className="font-display font-semibold text-sm">TMG Assistant</p>
              <p className="text-[10px] text-white/60 font-mono-sm uppercase tracking-[0.15em]">
                Typically replies instantly
              </p>
            </div>
            <button
              data-testid="chat-close-btn"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="p-1 hover:text-[var(--tmg-gold)] transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}-${i}`}
                className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-[var(--tmg-obsidian)] text-[var(--tmg-ivory)]"
                    : "mr-auto bg-[var(--tmg-ivory-2)]/70 text-[var(--tmg-obsidian)] border border-[var(--tmg-line)]"
                }`}
              >
                {m.text ? <MessageText text={m.text} /> : <Loader2 className="w-4 h-4 animate-spin text-[var(--tmg-gold)]" />}
              </div>
            ))}
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {QUICK_PROMPTS.map((q) => (
                  <button
                    key={q}
                    data-testid={`chat-chip-${q.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => send(q)}
                    className="text-xs border border-[var(--tmg-line)] px-3 py-2 hover:border-[var(--tmg-gold)] hover:text-[var(--tmg-gold-soft)] transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form
            className="border-t border-[var(--tmg-line)] p-3 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question..."
              maxLength={1000}
              className="flex-1 text-sm px-3 py-2.5 border border-[var(--tmg-line)] focus:outline-none focus:border-[var(--tmg-gold)] bg-white"
            />
            <button
              data-testid="chat-send-btn"
              type="submit"
              disabled={busy || !input.trim()}
              aria-label="Send message"
              className="w-10 h-10 bg-[var(--tmg-gold)] text-[var(--tmg-obsidian)] flex items-center justify-center disabled:opacity-40 hover:bg-[var(--tmg-gold-soft)] hover:text-white transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
